import { Button, ComboboxField, FormDialog, Notice, TextField } from "@don/design-system";
import type { AddTeammateRequest } from "@don/wire";
import { useState } from "react";

import { addFormMessages, addFormValid, addRequest, deactivatedMatch, normalEmail, WORKS_IN, type AddForm } from "./add.js";
import { offerCopy, type ReactivateTarget } from "./reactivate.js";

const INCOMPLETE = "Fill in every field to continue";

/**
 * What a send came to, once the page has acted on a success: done, the address on a current
 * person, or the address of a deactivated person (Add only, issue 218), who is named.
 */
export type Sent = "done" | "email_on_team" | { readonly emailDeactivated: ReactivateTarget };

export interface TeammateDialogProps {
  /** The hooks' prefix: the dialog is `data-team-{kind}-dialog`, its parts `data-{kind}-*`. */
  kind: "add" | "edit";
  title: string;
  initial: AddForm;
  /** The submit button's word, and its word while the request is out. */
  submitLabel: string;
  busyLabel: string;
  /** The in-dialog Notice when the request fails, from the form as sent. */
  failedCopy: (sent: AddForm) => string;
  /**
   * Sends the request. On a success the page acts on the answer (closing this dialog) and
   * resolves `done`; `email_on_team` keeps the dialog open on the address. A rejection is a
   * failed request.
   */
  send: (request: AddTeammateRequest) => Promise<Sent>;
  /**
   * The offer band's button (issue 218): reactivate the deactivated person whose address was
   * sent instead of adding someone new. Only Add passes it.
   */
  onReactivate?: (target: ReactivateTarget) => void;
  onClose: () => void;
}

/**
 * The teammate form Add and Edit share (issues 215 and 216; design-spec §5 `add-form`,
 * `edit-form`, ux-spec decision 20 "Edit is the Add form prefilled"): Name, Email and Works in
 * on the `FormDialog` shell, 480 wide, Name focused. The rules are `add.ts`'s; this owns only
 * the dialog's states, and `AddTeammateDialog` and `EditTeammateDialog` say what differs.
 *
 * - **Submit is disabled until all three are valid**, with the reason as its `title`. Return in
 *   a field submits when it is enabled, except in Works in while its list is open, where Return
 *   picks the active place (and Escape closes only the list).
 * - **Busy** (§11 D2): the submit reads its busy word and Cancel, Escape and the scrim do
 *   nothing until the server answers, so nothing is closed under a write that may still land.
 * - **`email_on_team`**: "Already on your team." under Email, submit disabled until the
 *   address changes, focus back on Email.
 * - **`emailDeactivated`** (issue 218, design-spec §5 `add-form-deactivated-match`): the offer
 *   band under Email instead of an error, "{Full name} was deactivated on {Mon D}. Reactivate
 *   {First}?" with "Reactivate {First}", submit disabled until the address changes, focus back
 *   on Email.
 * - **A failed request** keeps every value and shows the Notice; it clears when Name or Email
 *   changes.
 * - Otherwise Escape, Cancel and the scrim close it and discard what was typed (§11 D6).
 */
export function TeammateDialog({
  kind, title, initial, submitLabel, busyLabel, failedCopy, send, onReactivate, onClose,
}: TeammateDialogProps) {
  const [form, setForm] = useState<AddForm>(initial);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const messages = addFormMessages(form);
  const valid = addFormValid(form);
  const scope = `[data-team-${kind}-dialog]`;

  /** The design system's controls take no ref (React 18), so focus is moved by selector. */
  const focus = (selector: string) => document.querySelector<HTMLElement>(`${scope} ${selector}`)?.focus();

  async function submit() {
    if (!valid || busy) return;
    const sent = form;
    setBusy(true);
    setFailed(null);
    try {
      const outcome = await send(addRequest(sent));
      if (outcome === "done") return;
      const email = normalEmail(sent.email);
      setForm((now) =>
        outcome === "email_on_team"
          ? { ...now, onTeam: email, touched: { ...now.touched, email: true } }
          : { ...now, deactivated: { email, target: outcome.emailDeactivated }, touched: { ...now.touched, email: true } },
      );
      setBusy(false);
      // Submit is disabled again, so the focus it held goes back to the field to fix.
      requestAnimationFrame(() => focus(`input[data-${kind}-email]`));
    } catch {
      setFailed(failedCopy(sent));
      setBusy(false);
      requestAnimationFrame(() => focus(`[data-${kind}-submit]`));
    }
  }

  const close = () => {
    if (!busy) onClose();
  };
  const edit = (field: "name" | "email", value: string) => {
    setForm((now) => ({ ...now, [field]: value }));
    // The Notice is about what was sent; a changed Name or Email is a new request.
    setFailed(null);
  };
  const leave = (field: "name" | "email") => setForm((now) => ({ ...now, touched: { ...now.touched, [field]: true } }));
  const hook = (part: string) => ({ [`data-${kind}-${part}`]: "" });
  const match = onReactivate === undefined ? null : deactivatedMatch(form);
  const offer = match === null ? null : offerCopy(match);

  return (
    <FormDialog
      title={title}
      width={480}
      initialFocus={`input[data-${kind}-name]`}
      onEscape={close}
      onScrim={close}
      onEnter={() => void submit()}
      {...{ [`data-team-${kind}-dialog`]: "" }}
      data-busy={busy ? "" : undefined}
      actions={
        <>
          <Button variant="secondary" {...hook("cancel")} disabled={busy} onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            {...hook("submit")}
            disabled={!valid || busy}
            title={!valid && !busy ? INCOMPLETE : undefined}
            onClick={() => void submit()}
          >
            {busy ? busyLabel : submitLabel}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
        <TextField
          label="Name"
          {...hook("name")}
          autoComplete="off"
          value={form.name}
          error={messages.name}
          onChange={(event) => edit("name", event.target.value)}
          onBlur={() => leave("name")}
        />
        <TextField
          label="Email"
          {...hook("email")}
          type="email"
          autoComplete="off"
          spellCheck={false}
          value={form.email}
          error={messages.email}
          onChange={(event) => edit("email", event.target.value)}
          onBlur={() => leave("email")}
        />
        {match !== null && offer !== null && (
          // design-spec §4 "Offer band": under Email, a tone panel (which gives the secondary
          // its sheet fill and edge), text 14 ink-1, the button secondary sm.
          <div
            {...hook("offer")}
            data-tone-panel=""
            role="status"
            style={{
              display: "flex", alignItems: "center", gap: "var(--space-2)",
              marginTop: "calc(var(--space-1) - var(--space-2))",
              padding: "var(--space-15) var(--space-15) var(--space-15) var(--space-2)",
              borderRadius: 6, background: "var(--tone)",
              fontSize: "var(--type-small-size)", lineHeight: 1.45, color: "var(--ink-1)",
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>{offer.sentence}</span>
            <Button variant="secondary" size="sm" {...hook("reactivate")} onClick={() => onReactivate?.(match)}>
              {offer.button}
            </Button>
          </div>
        )}
        {/* A searchable combobox (owner changed 2026-10-05): it only ever commits an option. */}
        <ComboboxField
          label="Works in"
          {...hook("works-in")}
          placeholder="Choose a place"
          options={WORKS_IN}
          value={form.worksIn}
          onChange={(value) => setForm((now) => ({ ...now, worksIn: value as AddForm["worksIn"] }))}
        />
      </div>
      {failed !== null && (
        <div {...hook("failed")} style={{ marginTop: "var(--space-3)" }}>
          <Notice tone="failed">{failed}</Notice>
        </div>
      )}
    </FormDialog>
  );
}
